import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";
import {
  extractFlattenedFieldAPI,
  isValidField as isValidFieldAPI,
  formatDateAPI,
} from "../../../../Utils/WatchlistSummary/validation";

// ===== TYPES & INTERFACES =====

interface SectionData {
  sectionScore?: number;
  sectionHealthFlag?: string;
  sectionRelevanceFlag?: string;
}

interface Platform {
  platform?: string;
  followers?: number | string;
}

interface EngagementMetric {
  platform?: string;
  engagement_rate?: number | string;
}

interface RecentPost {
  title?: string;
  date?: string;
  platform?: string;
  sentiment?: string;
}

interface PopularTopic {
  topic?: string;
  mentions?: number | string;
  sentiment?: string;
}

interface SocialSectionProps {
  data: any;
  sectionData: any;
  accountName: string;
  organizationName?: string;
  onToggleAudio?: () => void;
}

// ===== SCORE BUBBLE COMPONENT =====

const SocialScoreBubble: React.FC<{ score: number }> = ({ score }) => {
  const getScoreColors = (score: number) => businessHealthScorePalette(score);

  const colors = getScoreColors(score);

  return (
    <div style={{ position: "relative", width: 48, height: 48, flexShrink: 0 }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: colors.outer,
          border: `2px solid ${colors.inner}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 6,
          left: 6,
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: colors.inner,
          border: `1px solid ${colors.score}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -2,
          right: -2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: colors.score,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {Math.round(score)}
        </span>
      </div>
    </div>
  );
};

// ===== MODAL HEADER COMPONENT =====

const SocialModalHeader: React.FC<{
  accountName: string;
  sectionData: SectionData;
  onToggleAudio?: () => void;
}> = ({ accountName, sectionData, onToggleAudio }) => {
  const sectionScore = sectionData.sectionScore || 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "16px",
      }}
    >
      <SocialScoreBubble score={sectionScore} />
      <div>
        <h3 style={{ margin: 0, fontSize: 18, color: "#8b5cf6" }}>
          <strong>{accountName}</strong> - <em>Social Media Analysis</em>
        </h3>
      </div>
      <button
        id="social-audio-btn"
        title="Play social media audio narration"
        onClick={onToggleAudio}
        style={{
          background: "none",
          border: "none",
          padding: 8,
          borderRadius: 6,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#8b5cf6",
        }}
      >
        <svg
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 5 6 9H2v6h4l5 4V5Z" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </button>
    </div>
  );
};

// ===== MODAL BODY COMPONENT =====

const SocialModalBody: React.FC<{
  synopsis?: string | null;
  confidenceScore?: number | null;
  dataFreshness?: string | null;
  lastUpdated?: string | null;
  sources?: any[];
  sectionData: SectionData;
}> = ({ synopsis, confidenceScore, dataFreshness, sources, sectionData }) => {
  const healthFlag = sectionData.sectionHealthFlag || "N/A";
  const relevanceFlag = sectionData.sectionRelevanceFlag || "N/A";

  const sourcesText = Array.isArray(sources)
    ? sources
        .map(s => (typeof s === "object" ? s?.name : s))
        .filter(Boolean)
        .join(", ") || "Multiple social platforms"
    : "Multiple social platforms";

  const formatConfidenceScore = (score: number | null | undefined) => {
    if (score === null || score === undefined)
      return { display: "N/A", color: "#64748b" };
    if (typeof score === "number") {
      let percentValue;
      if (score <= 1) percentValue = Math.round(score * 100);
      else if (score <= 10) percentValue = Math.round(score * 10);
      else percentValue = Math.round(score);
      const display = `${percentValue}%`;
      let color = "#dc2626";
      if (percentValue >= 80) color = "#16a34a";
      else if (percentValue >= 60) color = "#d97706";
      return { display, color };
    }
    return { display: "N/A", color: "#64748b" };
  };

  const formatDataFreshness = (freshness: any) => {
    if (!freshness) return { display: "N/A", color: "#64748b" };
    const freshnessStr = String(freshness).toLowerCase();
    let color = "#64748b";
    if (freshnessStr.includes("recent") || freshnessStr.includes("up-to-date"))
      color = "#16a34a";
    else if (
      freshnessStr.includes("moderate") ||
      freshnessStr.includes("weekly")
    )
      color = "#d97706";
    else if (
      freshnessStr.includes("stale") ||
      freshnessStr.includes("outdated")
    )
      color = "#dc2626";
    return { display: freshness, color };
  };

  const confidenceData = formatConfidenceScore(confidenceScore);
  const freshnessData = formatDataFreshness(dataFreshness);

  const getHealthText = (flag: string) => {
    switch (flag) {
      case "G":
        return "GREEN";
      case "Y":
        return "YELLOW";
      case "R":
        return "RED";
      default:
        return "N/A";
    }
  };

  const getRelevanceText = (flag: string) => {
    switch (flag) {
      case "H":
        return "HIGH";
      case "M":
        return "MEDIUM";
      case "L":
        return "LOW";
      default:
        return "N/A";
    }
  };

  const badgeStyle = (bgColor: string) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px",
    background: bgColor,
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    color: "white",
  });

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "12px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={badgeStyle(
            healthFlag === "G"
              ? "#16a34a"
              : healthFlag === "Y"
                ? "#d97706"
                : "#dc2626"
          )}
        >
          <span>Account Health:</span>
          <strong>{getHealthText(healthFlag)}</strong>
        </div>
        <div style={badgeStyle("#8b5cf6")}>
          <span>Relevance to Smarsh:</span>
          <strong>{getRelevanceText(relevanceFlag)}</strong>
        </div>
      </div>

      {synopsis ? (
        <div style={{ padding: 16 }}>
          <p
            style={{
              margin: "0 0 12px 0",
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {synopsis}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "20% 30% 50%",
              gap: 12,
              marginTop: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Confidence:
              </span>
              <span
                style={{
                  color: confidenceData.color,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {confidenceData.display}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span
                style={{ color: "#64748b", fontWeight: 500, marginBottom: 2 }}
              >
                Freshness:
              </span>
              <span
                style={{
                  color: freshnessData.color,
                  fontWeight: 600,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                }}
              >
                {freshnessData.display}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span
                style={{ color: "#64748b", fontWeight: 500, marginBottom: 2 }}
              >
                Sources:
              </span>
              <span
                style={{
                  color: "#1e293b",
                  fontWeight: 600,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                }}
              >
                {sourcesText}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: "#64748b",
              fontStyle: "italic",
            }}
          >
            No social media analysis available.
          </p>
        </div>
      )}
    </div>
  );
};

// ===== ORGANIZATION RELEVANCE CARD =====

const OrganizationRelevanceCard: React.FC<{
  relevance?: any;
  organizationName?: string;
  orgCount?: number | null;
  orgDirectMentions?: number | null;
  orgContextualMentions?: number | null;
  orgSentiment?: string | null;
  orgKeyPosts?: any[];
}> = ({
  relevance,
  organizationName,
  orgCount,
  orgDirectMentions,
  orgContextualMentions,
  orgSentiment,
  orgKeyPosts,
}) => {
  const orgName = organizationName || "Organization";

  const getEnhancedRelevance = (relevanceValue: any) => {
    if (!relevanceValue) return null;
    const relevanceStr = String(relevanceValue).trim().toLowerCase();
    if (relevanceStr === "low")
      return `Relevance of the account's business to ${orgName} is low.`;
    if (relevanceStr === "medium")
      return `Relevance of the account's business to ${orgName} is medium.`;
    if (relevanceStr === "high")
      return `Relevance of the account's business to ${orgName} is high.`;
    return relevanceValue;
  };

  const enhancedRelevance = getEnhancedRelevance(relevance);
  const hasMentionsData =
    isValidFieldAPI(orgCount) ||
    isValidFieldAPI(orgDirectMentions) ||
    isValidFieldAPI(orgContextualMentions) ||
    isValidFieldAPI(orgSentiment) ||
    (Array.isArray(orgKeyPosts) && orgKeyPosts.length > 0);

  if (!enhancedRelevance && !hasMentionsData) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🎯</span> Relevance for {orgName}
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        {enhancedRelevance && (
          <p
            style={{
              margin: "0 0 16px 0",
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {enhancedRelevance}
          </p>
        )}

        {hasMentionsData && (
          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>🏢</span> {orgName} Mentions
            </h5>
            {orgCount && orgCount > 0 ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    padding: 12,
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: 4,
                    }}
                  >
                    Total Mentions
                  </div>
                  <div
                    style={{ fontSize: 18, fontWeight: 700, color: "#8b5cf6" }}
                  >
                    {orgCount}
                  </div>
                </div>
                {isValidFieldAPI(orgDirectMentions) && (
                  <div
                    style={{
                      padding: 12,
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      Direct Mentions
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#8b5cf6",
                      }}
                    >
                      {orgDirectMentions}
                    </div>
                  </div>
                )}
                {isValidFieldAPI(orgContextualMentions) && (
                  <div
                    style={{
                      padding: 12,
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      Contextual
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#8b5cf6",
                      }}
                    >
                      {orgContextualMentions}
                    </div>
                  </div>
                )}
                {isValidFieldAPI(orgSentiment) && (
                  <div
                    style={{
                      padding: 12,
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      Sentiment
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        padding: "4px 8px",
                        borderRadius: 4,
                        display: "inline-block",
                        background:
                          orgSentiment === "positive"
                            ? "#dcfce7"
                            : orgSentiment === "negative"
                              ? "#fee2e2"
                              : "#f3f4f6",
                        color:
                          orgSentiment === "positive"
                            ? "#166534"
                            : orgSentiment === "negative"
                              ? "#991b1b"
                              : "#4b5563",
                      }}
                    >
                      {orgSentiment}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: "#6b7280",
                  fontStyle: "italic",
                }}
              >
                No mentions of {orgName} found in recent social media activity.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ===== SENTIMENT ANALYSIS CARD =====

const SentimentAnalysisCard: React.FC<{
  neutralRatio?: number | null;
  negativeRatio?: number | null;
  positiveRatio?: number | null;
}> = ({ neutralRatio, negativeRatio, positiveRatio }) => {
  const hasData =
    isValidFieldAPI(neutralRatio) ||
    isValidFieldAPI(negativeRatio) ||
    isValidFieldAPI(positiveRatio);
  if (!hasData) return null;

  const formatRatio = (ratio: any) => {
    if (!isValidFieldAPI(ratio)) return "N/A";
    const numValue =
      typeof ratio === "string" ? parseFloat(ratio.replace("%", "")) : ratio;
    if (isNaN(numValue)) return "N/A";
    const finalValue = numValue <= 1 ? numValue * 100 : numValue;
    return `${finalValue.toFixed(1)}%`;
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>😊</span> Sentiment Analysis
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: 12,
          }}
        >
          {isValidFieldAPI(positiveRatio) && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#dcfce7",
                border: "1px solid #bbf7d0",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#16a34a",
                  marginBottom: 4,
                }}
              >
                {formatRatio(positiveRatio)}
              </div>
              <div style={{ fontSize: 12, color: "#166534", fontWeight: 500 }}>
                Positive
              </div>
            </div>
          )}
          {isValidFieldAPI(neutralRatio) && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#f3f4f6",
                border: "1px solid #d1d5db",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#6b7280",
                  marginBottom: 4,
                }}
              >
                {formatRatio(neutralRatio)}
              </div>
              <div style={{ fontSize: 12, color: "#4b5563", fontWeight: 500 }}>
                Neutral
              </div>
            </div>
          )}
          {isValidFieldAPI(negativeRatio) && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#fee2e2",
                border: "1px solid #fecaca",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#dc2626",
                  marginBottom: 4,
                }}
              >
                {formatRatio(negativeRatio)}
              </div>
              <div style={{ fontSize: 12, color: "#991b1b", fontWeight: 500 }}>
                Negative
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== PLATFORM PERFORMANCE CARD =====

const PlatformPerformanceCard: React.FC<{
  platforms?: Platform[] | Record<string, any>;
  engagementMetrics?: EngagementMetric[] | Record<string, any>;
}> = ({ platforms, engagementMetrics }) => {
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "object" && !Array.isArray(value)) {
      return "N/A"; // Objects should be handled separately
    }
    return String(value);
  };

  const normalizePlatforms = (data: any): Platform[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === "object" && !Array.isArray(data)) {
      return Object.entries(data).map(([key, value]) => {
        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          const val = value as any;
          return {
            platform: val.platform || key,
            followers: val.followers,
          };
        }
        return { platform: key, followers: value };
      });
    }
    return [];
  };

  const normalizeEngagementMetrics = (data: any): EngagementMetric[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === "object" && !Array.isArray(data)) {
      return Object.entries(data).map(([key, value]) => {
        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          const val = value as any;
          return {
            platform: val.platform || key,
            engagement_rate: val.engagement_rate,
          };
        }
        return { platform: key, engagement_rate: value };
      });
    }
    return [];
  };

  const platformsArray = normalizePlatforms(platforms);
  const engagementMetricsArray = normalizeEngagementMetrics(engagementMetrics);

  const hasData =
    platformsArray.length > 0 || engagementMetricsArray.length > 0;
  if (!hasData) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📊</span> Platform Performance
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
          }}
        >
          {platformsArray.map((platform, i) => (
            <div
              key={i}
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            >
              <div
                style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
              >
                {platform.platform || "Platform"}
              </div>
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                Followers: {formatValue(platform.followers)}
              </div>
            </div>
          ))}
          {engagementMetricsArray.map((metric, i) => (
            <div
              key={i}
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            >
              <div
                style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
              >
                {metric.platform || "Platform"}
              </div>
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                Engagement: {formatValue(metric.engagement_rate)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== ENGAGEMENT TRENDS CARD =====

const EngagementTrendsCard: React.FC<{
  trendComparison?: string | null;
  monthlyPostVolume?: number | string | null | Record<string, any>;
  monthlyEngagementRate?: number | string | null | Record<string, any>;
}> = ({ trendComparison, monthlyPostVolume, monthlyEngagementRate }) => {
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "object" && !Array.isArray(value)) {
      return "N/A"; // Objects should be handled separately
    }
    return String(value);
  };

  const isObjectValue = (value: any): boolean => {
    return (
      value !== null &&
      value !== undefined &&
      typeof value === "object" &&
      !Array.isArray(value)
    );
  };

  const hasData =
    isValidFieldAPI(trendComparison) ||
    isValidFieldAPI(monthlyPostVolume) ||
    isValidFieldAPI(monthlyEngagementRate);
  if (!hasData) return null;

  const postVolumeIsObject = isObjectValue(monthlyPostVolume);
  const engagementRateIsObject = isObjectValue(monthlyEngagementRate);

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📈</span> Engagement Trends
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        {isValidFieldAPI(trendComparison) && (
          <p
            style={{
              margin: "0 0 12px 0",
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {trendComparison}
          </p>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
          }}
        >
          {isValidFieldAPI(monthlyPostVolume) &&
            (postVolumeIsObject ? (
              Object.entries(monthlyPostVolume as Record<string, any>).map(
                ([platform, value]) => (
                  <div
                    key={platform}
                    style={{
                      padding: 12,
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      {platform} - Monthly Posts
                    </div>
                    <div style={{ fontSize: 14, color: "#6b7280" }}>
                      {formatValue(value)}
                    </div>
                  </div>
                )
              )
            ) : (
              <div
                style={{
                  padding: 12,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
                >
                  Monthly Posts
                </div>
                <div style={{ fontSize: 14, color: "#6b7280" }}>
                  {formatValue(monthlyPostVolume)}
                </div>
              </div>
            ))}
          {isValidFieldAPI(monthlyEngagementRate) &&
            (engagementRateIsObject ? (
              Object.entries(monthlyEngagementRate as Record<string, any>).map(
                ([platform, value]) => (
                  <div
                    key={platform}
                    style={{
                      padding: 12,
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      {platform} - Engagement Rate
                    </div>
                    <div style={{ fontSize: 14, color: "#6b7280" }}>
                      {formatValue(value)}
                    </div>
                  </div>
                )
              )
            ) : (
              <div
                style={{
                  padding: 12,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}
                >
                  Engagement Rate
                </div>
                <div style={{ fontSize: 14, color: "#6b7280" }}>
                  {formatValue(monthlyEngagementRate)}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

// ===== RISKS & OPPORTUNITIES CARD =====

const RisksOpportunitiesCard: React.FC<{
  risks?: string[];
  opportunities?: string[];
}> = ({ risks, opportunities }) => {
  const hasRisks = Array.isArray(risks) && risks.length > 0;
  const hasOpportunities =
    Array.isArray(opportunities) && opportunities.length > 0;
  if (!hasRisks && !hasOpportunities) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>⚖️</span> Risks & Opportunities
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          {hasRisks && (
            <div>
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#dc2626",
                }}
              >
                Risks
              </h5>
              <div>
                {risks.map((risk, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "8px 12px",
                      background: "#fee2e2",
                      border: "1px solid #fecaca",
                      borderRadius: 6,
                      marginBottom: 6,
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        color: "#991b1b",
                        lineHeight: 1.4,
                      }}
                    >
                      {risk}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasOpportunities && (
            <div>
              <h5
                style={{
                  margin: "0 0 8px 0",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#16a34a",
                }}
              >
                Opportunities
              </h5>
              <div>
                {opportunities.map((opportunity, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "8px 12px",
                      background: "#dcfce7",
                      border: "1px solid #bbf7d0",
                      borderRadius: 6,
                      marginBottom: 6,
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        color: "#166534",
                        lineHeight: 1.4,
                      }}
                    >
                      {opportunity}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== RECENT POSTS CARD =====

const RecentPostsCard: React.FC<{ recentPosts?: RecentPost[] }> = ({
  recentPosts,
}) => {
  if (!Array.isArray(recentPosts) || recentPosts.length === 0) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📝</span> Recent Posts
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div>
          {recentPosts.slice(0, 5).map((post, i) => (
            <div
              key={i}
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 600, color: "#374151", flex: 1 }}>
                  {post.title || "Post"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginLeft: 12 }}>
                  {post.date ? formatDateAPI(post.date) : ""}
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#8b5cf6",
                  fontWeight: 500,
                  marginBottom: 4,
                }}
              >
                {post.platform || "Platform"}
              </div>
              <div
                style={{
                  fontSize: 12,
                  padding: "4px 8px",
                  borderRadius: 4,
                  display: "inline-block",
                  background:
                    post.sentiment === "positive"
                      ? "#dcfce7"
                      : post.sentiment === "negative"
                        ? "#fee2e2"
                        : "#f3f4f6",
                  color:
                    post.sentiment === "positive"
                      ? "#166534"
                      : post.sentiment === "negative"
                        ? "#991b1b"
                        : "#4b5563",
                }}
              >
                {post.sentiment || "neutral"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== POPULAR TOPICS CARD =====

const PopularTopicsCard: React.FC<{ popularTopics?: PopularTopic[] }> = ({
  popularTopics,
}) => {
  if (!Array.isArray(popularTopics) || popularTopics.length === 0) return null;

  const getMentionsColor = (mentions: any) => {
    if (typeof mentions === "string") {
      const mentionsLower = mentions.toLowerCase();
      if (mentionsLower.includes("high")) return "#16a34a";
      if (mentionsLower.includes("moderate")) return "#d97706";
      if (mentionsLower.includes("low")) return "#dc2626";
    }
    const count = parseInt(mentions) || 0;
    if (count >= 100) return "#16a34a";
    if (count >= 50) return "#d97706";
    return "#dc2626";
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🔥</span> Popular Topics
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div>
          {popularTopics.slice(0, 5).map((topic, i) => (
            <div
              key={i}
              style={{
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 600, color: "#374151", flex: 1 }}>
                  {topic.topic || "Topic"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: getMentionsColor(topic.mentions),
                    fontWeight: 600,
                    marginLeft: 12,
                  }}
                >
                  {topic.mentions || 0} mentions
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  padding: "4px 8px",
                  borderRadius: 4,
                  display: "inline-block",
                  background:
                    topic.sentiment === "positive"
                      ? "#dcfce7"
                      : topic.sentiment === "negative"
                        ? "#fee2e2"
                        : "#f3f4f6",
                  color:
                    topic.sentiment === "positive"
                      ? "#166534"
                      : topic.sentiment === "negative"
                        ? "#991b1b"
                        : "#4b5563",
                }}
              >
                {topic.sentiment || "neutral"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===== MAIN SOCIAL SECTION COMPONENT =====

export const SocialSection: React.FC<SocialSectionProps> = ({
  data,
  sectionData,
  accountName,
  organizationName,
  onToggleAudio,
}) => {
  if (!data) return <div>No social media data available</div>;

  const synopsis = extractFlattenedFieldAPI(data, "synopsis", null);
  const confidenceScore = extractFlattenedFieldAPI(
    data,
    "metadata.confidence_score",
    null
  );
  const lastUpdated = extractFlattenedFieldAPI(
    data,
    "metadata.last_updated",
    null
  );
  const dataFreshness = extractFlattenedFieldAPI(
    data,
    "metadata.data_freshness",
    null
  );
  const sources = extractFlattenedFieldAPI(data, "metadata.sources", []);

  const platforms = extractFlattenedFieldAPI(data, "platforms", []);
  const recentPosts = extractFlattenedFieldAPI(
    data,
    "platforms.recent_post",
    []
  );
  const engagementMetrics = extractFlattenedFieldAPI(
    data,
    "platforms.engagement_metrics",
    []
  );

  const neutralRatio = extractFlattenedFieldAPI(
    data,
    "social_media_metrics.sentiment_analysis.neutral_ratio",
    null
  );
  const negativeRatio = extractFlattenedFieldAPI(
    data,
    "social_media_metrics.sentiment_analysis.negative_ratio",
    null
  );
  const positiveRatio = extractFlattenedFieldAPI(
    data,
    "social_media_metrics.sentiment_analysis.positive_ratio",
    null
  );

  const trendComparison = extractFlattenedFieldAPI(
    data,
    "engagement_trends.trend_comparison",
    null
  );
  const monthlyPostVolume = extractFlattenedFieldAPI(
    data,
    "engagement_trends.monthly_post_volume",
    null
  );
  const monthlyEngagementRate = extractFlattenedFieldAPI(
    data,
    "engagement_trends.monthly_engagement_rate",
    null
  );

  const popularTopics = extractFlattenedFieldAPI(
    data,
    "risks_opportunities.popular_topics",
    []
  );
  const risks = extractFlattenedFieldAPI(data, "risks_opportunities.risks", []);
  const opportunities = extractFlattenedFieldAPI(
    data,
    "risks_opportunities.opportunities",
    []
  );

  const orgRelevance = extractFlattenedFieldAPI(
    data,
    "organizationMentions.relevance",
    null
  );
  const orgCount = extractFlattenedFieldAPI(
    data,
    "organizationMentions.count",
    null
  );
  const orgDirectMentions = extractFlattenedFieldAPI(
    data,
    "organizationMentions.directMentions",
    null
  );
  const orgContextualMentions = extractFlattenedFieldAPI(
    data,
    "organizationMentions.contextualMentions",
    null
  );
  const orgSentiment = extractFlattenedFieldAPI(
    data,
    "organizationMentions.sentiment",
    null
  );
  const orgKeyPosts = extractFlattenedFieldAPI(
    data,
    "organizationMentions.keyPosts",
    []
  );

  return (
    <div className="social-section-consolidated">
      <SocialModalHeader
        accountName={accountName}
        sectionData={sectionData}
        onToggleAudio={onToggleAudio}
      />
      <SocialModalBody
        synopsis={synopsis}
        confidenceScore={confidenceScore}
        dataFreshness={dataFreshness}
        lastUpdated={lastUpdated}
        sources={sources}
        sectionData={sectionData}
      />
      <OrganizationRelevanceCard
        relevance={orgRelevance}
        organizationName={organizationName}
        orgCount={orgCount}
        orgDirectMentions={orgDirectMentions}
        orgContextualMentions={orgContextualMentions}
        orgSentiment={orgSentiment}
        orgKeyPosts={orgKeyPosts}
      />
      <SentimentAnalysisCard
        neutralRatio={neutralRatio}
        negativeRatio={negativeRatio}
        positiveRatio={positiveRatio}
      />
      <PlatformPerformanceCard
        platforms={platforms}
        engagementMetrics={engagementMetrics}
      />
      <EngagementTrendsCard
        trendComparison={trendComparison}
        monthlyPostVolume={monthlyPostVolume}
        monthlyEngagementRate={monthlyEngagementRate}
      />
      <RisksOpportunitiesCard risks={risks} opportunities={opportunities} />
      <RecentPostsCard recentPosts={recentPosts} />
      <PopularTopicsCard popularTopics={popularTopics} />
    </div>
  );
};
